import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Users, Printer, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import { exportToExcel } from '../../utils/excelExport';

const CreditReportPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [creditData, setCreditData] = useState(null);
  const [creditBranch, setCreditBranch] = useState('all');
  const [creditStatus, setCreditStatus] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/branches').then(r => setBranches(r.data)).catch(() => {}); }, []);

  const fetchCreditReport = async () => {
    setLoading(true);
    try {
      const params = { loan_status: creditStatus };
      if (isAdmin() && creditBranch !== 'all') params.branch_id = creditBranch;
      const resp = await api.get('/reports/credit-report', { params });
      setCreditData(resp.data);
    } catch { toast.error('Failed to fetch Credit Report'); }
    finally { setLoading(false); }
  };

  const getBranchName = () => creditBranch === 'all' ? 'All Branches' : branches.find(b => b.id === creditBranch)?.name || 'Unknown';

  const handleExcel = () => {
    if (!creditData) { toast.error('Generate the report first'); return; }
    const rows = (creditData.entrepreneurs || []).map((e, i) => ({
      'SN': i + 1,
      'Entrepreneur': e.entrepreneur_name,
      'Mobile': e.mobile || '',
      'Branch': e.branch_code,
      'SHG/VLF': e.shg_vlf_name || '',
      'Loan IDs': (e.loans || []).map(l => l.loan_id).join(', '),
      'Loan Count': (e.loans || []).length,
      'Total Loan Amount': Number(e.total_loan_amount || 0),
      'Collected': Number(e.total_collected || 0),
      'Outstanding': Number(e.total_outstanding || 0),
      'Missed EMIs': e.total_missed_emis || 0,
      'Repayment %': e.repayment_percentage || 0,
      'Credit Score': e.credit_score,
      'Eligible for 2nd Loan': e.eligible_for_next_loan ? 'Yes' : 'No',
    }));
    const s = creditData.summary || {};
    exportToExcel({
      filename: `WWAGS_Credit_Report_${getBranchName().replace(/\s+/g,'_')}`,
      sheetName: 'Credit Report',
      rows,
      kpis: [
        { label: 'Branch', value: getBranchName() },
        { label: 'Total Entrepreneurs', value: s.total || 0 },
        { label: 'Good Credit', value: s.good || 0 },
        { label: 'Moderate', value: s.moderate || 0 },
        { label: 'Poor', value: s.poor || 0 },
      ],
    });
  };

  const handlePrint = () => {
    if (!creditData) return;
    const rows = creditData.entrepreneurs.map((e, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}"><td>${e.entrepreneur_name}<br/><small>${e.mobile}</small></td><td>${e.branch_code}</td><td>${e.shg_vlf_name}</td><td style="text-align:center">${e.loans.map(l=>l.loan_id).join(', ')}</td><td style="text-align:right">Rs.${e.total_loan_amount.toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${e.total_collected.toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${e.total_outstanding.toLocaleString('en-IN')}</td><td style="text-align:center;font-weight:bold;color:${e.total_missed_emis>3?'#dc2626':e.total_missed_emis>0?'#d97706':'#059669'}">${e.total_missed_emis}</td><td style="text-align:center;font-weight:bold;color:${e.repayment_percentage>=85?'#047857':e.repayment_percentage>=60?'#b45309':'#dc2626'}">${e.repayment_percentage}%</td><td style="text-align:center"><span style="background:${e.credit_score==='Good'?'#d1fae5':e.credit_score==='Moderate'?'#fef3c7':'#fee2e2'};padding:2px 8px;border-radius:10px;font-size:9px;font-weight:bold">${e.credit_score}</span></td><td style="text-align:center;font-weight:bold;color:${e.eligible_for_next_loan?'#059669':'#dc2626'}">${e.eligible_for_next_loan?'Yes':'No'}</td></tr>`).join('');
    const s = creditData.summary;
    const body = `<div class="cards"><div class="card"><small>Total</small><b>${s.total}</b></div><div class="card" style="background:#ecfdf5;border-color:#059669"><small>Good</small><b>${s.good}</b></div><div class="card" style="background:#fffbeb;border-color:#d97706"><small>Moderate</small><b>${s.moderate}</b></div><div class="card" style="background:#fef2f2;border-color:#dc2626"><small>Poor</small><b>${s.poor}</b></div></div><table><thead><tr><th>Entrepreneur</th><th>Branch</th><th>SHG/VLF</th><th>Loans</th><th>Total Loan</th><th>Collected</th><th>Outstanding</th><th>Missed EMIs</th><th>Repay %</th><th>Credit</th><th>2nd Loan</th></tr></thead><tbody>${rows}</tbody></table>`;
    openPrintWindow(buildPrintHTML('Entrepreneur Credit Report', body, getBranchName()));
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="credit-report-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div><h1 className="text-2xl font-bold text-slate-900">Entrepreneur Credit Report</h1><p className="text-sm text-slate-600">Repayment history, missed EMIs, creditworthiness</p></div>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-3">
        {isAdmin() && (
          <div className="min-w-[150px]">
            <Label className="text-xs text-red-700 mb-1 block">Branch</Label>
            <Select value={creditBranch} onValueChange={setCreditBranch}>
              <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Loan Status</label>
          <Select value={creditStatus} onValueChange={setCreditStatus}>
            <SelectTrigger className="w-32" data-testid="credit-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={fetchCreditReport} className="bg-red-700 hover:bg-red-800 text-white" data-testid="generate-credit-report">Generate Report</Button>
        {creditData && <Button variant="outline" onClick={handlePrint} data-testid="print-credit-report"><Printer className="w-4 h-4 mr-1" />Print</Button>}
        {creditData && <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="excel-credit-report"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>}
      </div>

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div></div>}

      {creditData && !loading && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center"><p className="text-xs text-slate-600 font-medium mb-1">Total Entrepreneurs</p><p className="text-2xl font-bold text-slate-800">{creditData.summary.total}</p></div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center"><p className="text-xs text-emerald-600 font-medium mb-1">Good Credit</p><p className="text-2xl font-bold text-emerald-800">{creditData.summary.good}</p><p className="text-xs text-emerald-500">Eligible for 2nd Loan</p></div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center"><p className="text-xs text-amber-600 font-medium mb-1">Moderate</p><p className="text-2xl font-bold text-amber-800">{creditData.summary.moderate}</p><p className="text-xs text-amber-500">Conditionally Eligible</p></div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center"><p className="text-xs text-red-600 font-medium mb-1">Poor Credit</p><p className="text-2xl font-bold text-red-800">{creditData.summary.poor}</p><p className="text-xs text-red-500">Not Eligible</p></div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-red-700 text-white">
                    <th className="px-3 py-2 text-left">Entrepreneur</th>
                    <th className="px-3 py-2 text-left">Branch</th>
                    <th className="px-3 py-2 text-left">SHG/VLF</th>
                    <th className="px-3 py-2 text-center">Loans</th>
                    <th className="px-3 py-2 text-right">Total Loan</th>
                    <th className="px-3 py-2 text-right">Collected</th>
                    <th className="px-3 py-2 text-right">Outstanding</th>
                    <th className="px-3 py-2 text-center">Missed EMIs</th>
                    <th className="px-3 py-2 text-center">Repayment %</th>
                    <th className="px-3 py-2 text-center">Credit Score</th>
                    <th className="px-3 py-2 text-center">2nd Loan</th>
                  </tr>
                </thead>
                <tbody>
                  {creditData.entrepreneurs.map((ent, idx) => (
                    <tr key={ent.entrepreneur_id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-100`}>
                      <td className="px-3 py-2">
                        <Link to={`/entrepreneurs/${ent.entrepreneur_id}`} className="font-medium text-blue-700 hover:underline">{ent.entrepreneur_name}</Link>
                        <p className="text-slate-400">{ent.mobile}</p>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{ent.branch_code}</td>
                      <td className="px-3 py-2 text-slate-600">{ent.shg_vlf_name}</td>
                      <td className="px-3 py-2 text-center">
                        {ent.loans.map(l => (
                          <span key={l.loan_id} className="block">
                            <Link to={`/loans/${l.loan_id}`} className="text-purple-600 hover:underline">{l.loan_id}</Link>
                            <span className={`ml-1 text-[10px] px-1 rounded ${l.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{l.status}</span>
                          </span>
                        ))}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">Rs.{ent.total_loan_amount.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">Rs.{ent.total_collected.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right text-red-600">Rs.{ent.total_outstanding.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-center"><span className={`font-bold ${ent.total_missed_emis > 3 ? 'text-red-600' : ent.total_missed_emis > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{ent.total_missed_emis}</span></td>
                      <td className="px-3 py-2 text-center"><span className={`font-bold ${ent.repayment_percentage >= 85 ? 'text-emerald-700' : ent.repayment_percentage >= 60 ? 'text-amber-700' : 'text-red-700'}`}>{ent.repayment_percentage}%</span></td>
                      <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${ent.credit_score === 'Good' ? 'bg-emerald-100 text-emerald-800' : ent.credit_score === 'Moderate' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>{ent.credit_score}</span></td>
                      <td className="px-3 py-2 text-center">{ent.eligible_for_next_loan ? <span className="text-emerald-600 font-bold">Yes</span> : <span className="text-red-600 font-bold">No</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!creditData && !loading && (
        <div className="text-center py-12 text-slate-500"><Users className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>Click "Generate Report" to view entrepreneur credit report</p></div>
      )}
    </div>
  );
};

export default CreditReportPage;
